import { STATUS } from "@/constants/statuses";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import { CommonModule } from "@modules/common_module/CommonModule";
import {
    Observable,
    catchError,
    lastValueFrom,
    retry,
    switchMap,
    tap,
    timer,
} from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import NudgesApiService from "../api_services/NudgesApiService/NudgesApiService";
import NudgesEntity from "../entities/NudgesEntity";
import NudgesStore from "../stores/NudgesStore";
import { nudgesApplicationLoggerToken } from "../tokens";

@singleton()
class NudgesService {
    constructor(
        private readonly nudgesApiService: NudgesApiService,
        private readonly nudgesStore: NudgesStore,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(nudgesApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    //Api calls
    public getLastNudgeWithoutViewObservable(
        locale: string
    ): Observable<NudgesEntity> {
        this.logger.debug(
            "NudgesService.getLastNudgeWithoutViewObservable is started"
        );

        const status = this.nudgesStore.getStatus();
        this.nudgesStore.setStatus(getLoadingStatus(status));
        const page = this.commonModule.exports.locationController.getPathname();
        const pageWithoutPrefix =
            this.commonModule.exports.pathPrefixProvider.getUrlWithoutLocale(
                page
            );

        return timer(10_000, 60_000).pipe(
            switchMap(() =>
                this.nudgesApiService.getLastNudgeWithoutView({
                    page: pageWithoutPrefix,
                    locale,
                })
            ),
            catchError((error: unknown) => {
                this.logger.debug(
                    "NudgesService.getLastNudgeWithoutViewObservable is errored",
                    error
                );
                this.nudgesStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            retry(5),
            tap((value) => {
                this.logger.debug(
                    "NudgesService.getLastNudgeWithoutViewObservable is ready"
                );
                //Store Update
                applyTransaction(() => {
                    this.nudgesStore.update((state) => ({
                        ...state,
                        nudges: value,
                    }));
                    this.nudgesStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    public getLastNudgeWithoutView({ locale }: { locale: string }) {
        return this.getLastNudgeWithoutViewObservable(locale);
    }

    public async addViewed({ nudgeId }: { nudgeId: string }): Promise<void> {
        const page = this.commonModule.exports.locationController.getPathname();
        await lastValueFrom(
            this.nudgesApiService.addViewed({
                nudgeId,
                page,
            })
        );
    }

    public async addAction({ nudgeId }: { nudgeId: string }): Promise<void> {
        const page = this.commonModule.exports.locationController.getPathname();
        await lastValueFrom(
            this.nudgesApiService.addAction({
                nudgeId,
                page,
            })
        );
    }
}

export default NudgesService;
